import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

function PackageDetails() {
  const { slug } = useParams();
  const [packageData, setPackageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [bookingData, setBookingData] = useState({
    package_code: '',
    first_guest_name: '',
    total_persons: '',
    check_in_date: '',
    check_out_date: '',
    extra_bed: '',
    child_without_bed: '',
    meal_plan: '',
    mobile_no: '',
    additional_info: ''
  });

  const [submitting, setSubmitting] = useState(false);
  const [responseMsg, setResponseMsg] = useState('');

  const IMAGE_BASE_URL = 'https://travchap.com/admin/uploads/packages/';

  // ✅ Fetch package details
  useEffect(() => {
    const controller = new AbortController();

    const fetchPackage = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await fetch(
          `https://travchap.com/admin/api/package/${slug}`,
          { signal: controller.signal }
        );

        if (!res.ok) throw new Error(`HTTP error! Status: ${res.status}`);

        const result = await res.json();
        console.log('✅ Fetched package:', result);

        if (result && typeof result === 'object' && result.title) {
          setPackageData(result);
          setBookingData((prev) => ({
            ...prev,
            package_code: result.package_code || ''
          }));
        } else {
          console.error('Unexpected API structure:', result);
          throw new Error('Invalid API response structure');
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Fetch error:', err);
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPackage();

    return () => controller.abort();
  }, [slug]);

  // ✅ Handle form inputs
  const handleBookingChange = (e) => {
    const { name, value } = e.target;
    setBookingData((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Submit booking
  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setResponseMsg('');

    try {
      const formData = new FormData();
      Object.entries(bookingData).forEach(([key, value]) => {
        formData.append(key, value);
      });

      const res = await fetch('https://travchap.com/admin/api/booking', {
        method: 'POST',
        body: formData
      });

      const result = await res.json();

      if (res.ok) {
        setResponseMsg('✅ Booking submitted successfully!');
        setBookingData({
          package_code: '',
          first_guest_name: '',
          total_persons: '',
          check_in_date: '',
          check_out_date: '',
          extra_bed: '',
          child_without_bed: '',
          meal_plan: '',
          mobile_no: '',
          additional_info: ''
        });
      } else {
        setResponseMsg(
          result.message || '❌ Booking failed. Please try again.'
        );
      }
    } catch (err) {
      console.error(err);
      setResponseMsg('⚠️ Something went wrong. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading)
    return (
      <p className="text-center mt-10 text-gray-600 font-medium">Loading...</p>
    );
  if (error)
    return (
      <p className="text-center text-red-500 mt-10 font-semibold">{error}</p>
    );
  if (!packageData) return null;

  const {
    title,
    image,
    category,
    type,
    duration,
    description,
    meta_title,
    meta_description,
    inclusion,
    exclusion,
    note,
    addtional_charge,
    booking_payment_policy,
    booking_cancellation_policy,
    daywise_details_json,
    price_details_json,
    manage_stays
  } = packageData;

  const parsedPriceDetails = price_details_json
    ? JSON.parse(price_details_json)
    : [];
  const parsedDayDetails = daywise_details_json
    ? JSON.parse(daywise_details_json)
    : [];

  return (
    <div className="pb-10">
      {/* Hero Section */}
      <div
        className="relative w-full h-60 md:h-96 bg-cover bg-center flex items-center justify-center"
        style={{ backgroundImage: `url(${IMAGE_BASE_URL}${image})` }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative text-center text-white px-4 py-2">
          <h2 className="font-bold text-2xl md:text-4xl">{title}</h2>
          <p className="text-sm md:text-base mt-1 uppercase">
            {category} • {type}
          </p>
          <p className="text-sm md:text-base mt-1 uppercase">{duration}</p>
        </div>
      </div>

      <div className="p-6 md:p-10">
        {/* Meta Info */}
        <div className="text-center mb-8">
          <h3 className="text-xl font-semibold">{meta_title}</h3>
          <div
            className="text-gray-700 mt-2"
            dangerouslySetInnerHTML={{ __html: meta_description }}
          />
        </div>

        {/* Short Description */}
        <div
          className="text-gray-700 mb-6"
          dangerouslySetInnerHTML={{ __html: description }}
        />

        {/* Info Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300 text-sm md:text-base">
            <thead className="bg-blue-900 text-white">
              <tr>
                <th className="border p-2">Duration</th>
                <th className="border p-2">City</th>
                <th className="border p-2">Hotel Name</th>
                <th className="border p-2">Room Type</th>
                <th className="border p-2">Meal Plan</th>
              </tr>
            </thead>
            <tbody>
              {manage_stays?.map((hotel) => (
                <tr key={hotel.id} className="hover:bg-gray-100">
                  <td className="border p-2 text-center">
                    {hotel.nights} Nights
                  </td>
                  <td className="border p-2 text-center">{hotel.city_id}</td>
                  <td className="border p-2 text-center">{hotel.hotel_name}</td>
                  <td className="border p-2 text-center">{hotel.room_type}</td>
                  <td className="border p-2 text-center">{hotel.meal_plan}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pricing Table */}
        <div className="mt-6 overflow-x-auto">
          <table className="w-full border-collapse border border-gray-300 text-sm md:text-base">
            <thead className="bg-gray-200">
              <tr>
                <th className="border p-2">Package Cost </th>
                <th className="border p-2">Min 2 Persons</th>
                <th className="border p-2">4 Person</th>
                <th className="border p-2">6 Person</th>
                <th className="border p-2">Extra Bed</th>
                <th className="border p-2">Child Without Bed</th>
                <th className="border p-2">Booking Validity</th>
              </tr>
            </thead>
            <tbody>
              {parsedPriceDetails.map((price, index) => (
                <tr key={index} className="hover:bg-gray-100 text-center">
                  <td className="border p-2">Per Person Cost</td>
                  <td className="border p-2">₹{price.person_2}</td>
                  <td className="border p-2">₹{price.person_4}</td>
                  <td className="border p-2">₹{price.person_6}</td>
                  <td className="border p-2">₹{price.extra_bed}</td>
                  <td className="border p-2">₹{price.child_no_bed}</td>
                  <td className="border p-2">{price.booking_validity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Buttons */}
        <div className="flex justify-center gap-4 mt-6">
          <button
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            onClick={() => setShowModal(true)}
          >
            CUSTOMIZE YOUR PACKAGE
          </button>
          <button
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            onClick={() => setShowModal(true)}
          >
            BOOK NOW
          </button>
        </div>

        {/* Inclusion / Exclusion */}
        <div className="grid md:grid-cols-4 gap-6 mt-10 text-sm">
          <div
            className="bg-white shadow p-4 rounded-xl border border-transparent 
                  transition-all duration-300 
                  hover:shadow-2xl hover:-translate-y-2 hover:border-green-500"
          >
            <h3 className="font-bold text-xl mb-2">Package Inclusion</h3>
            <div dangerouslySetInnerHTML={{ __html: inclusion }} />
          </div>

          <div
            className="bg-white shadow p-4 rounded-xl border border-transparent 
                  transition-all duration-300 
                  hover:shadow-2xl hover:-translate-y-2 hover:border-green-500"
          >
            <h3 className="font-bold text-xl mb-2">Package Exclusion</h3>
            <div dangerouslySetInnerHTML={{ __html: exclusion }} />
          </div>

          <div
            className="bg-white shadow p-4 rounded-xl border border-transparent 
                  transition-all duration-300 
                  hover:shadow-2xl hover:-translate-y-2 hover:border-green-500"
          >
            <h3 className="font-bold text-xl mb-2">Note</h3>
            <div dangerouslySetInnerHTML={{ __html: note }} />
          </div>

          <div
            className="bg-white shadow p-4 rounded-xl border border-transparent 
                  transition-all duration-300 
                  hover:shadow-2xl hover:-translate-y-2 hover:border-green-500"
          >
            <h3 className="font-bold text-xl mb-2">Additional Charges</h3>
            <div dangerouslySetInnerHTML={{ __html: addtional_charge }} />
          </div>
        </div>
      </div>

      {/* Itinerary */}
      <div className="max-w-6xl mx-auto px-6 py-10">
        <h2 className="text-2xl font-bold text-center mb-6">
          DAY-WISE <span className="text-blue-600">ITINERARY</span>
        </h2>

        <div className="space-y-6 text-gray-700">
          {parsedDayDetails.map((item, index) => (
            <div
              key={index}
              className="border rounded-lg p-4 bg-white shadow-sm"
            >
              <h3 className="text-lg  mb-2 text-black font-bold">
                Day {item.day}
              </h3>

              {/* Render HTML content */}
              <div dangerouslySetInnerHTML={{ __html: item.detail }} />
            </div>
          ))}
        </div>
      </div>

      {/* Payment Policies */}
      <div className="max-w-6xl mx-auto px-6 space-y-8 mb-10">
        <div className="bg-gray-100 p-6 rounded-lg shadow">
          <h3 className="text-xl font-bold mb-4 text-center">
            BOOKING PAYMENT POLICY
          </h3>
          <div dangerouslySetInnerHTML={{ __html: booking_payment_policy }} />
        </div>
        <div className="bg-gray-100 p-6 rounded-lg shadow">
          <h3 className="text-xl font-bold mb-4 text-center">
            BOOKING CANCELLATION POLICY
          </h3>
          <div
            dangerouslySetInnerHTML={{ __html: booking_cancellation_policy }}
          />
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-start md:items-center z-50 overflow-y-auto">
          {/* Modal Container */}
          <div className="bg-[#083d56] text-white p-6 rounded-lg w-[95%] md:w-[90%] max-w-4xl relative mt-6 md:mt-20 overflow-y-auto max-h-[90vh]">
            {/* Close Button */}
            <button
              className="absolute top-2 right-2 text-white text-xl font-bold"
              onClick={() => setShowModal(false)}
            >
              ✖
            </button>

            {/* Title */}
            <h2 className="text-center text-lg md:text-2xl font-bold mb-6">
              Booking Now
            </h2>

            {/* Booking Form */}
            <form
              onSubmit={handleBookingSubmit}
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              {/* Dynamic Input Fields */}
             {[
              { label: 'Booking Code *', name: 'package_code', type: 'text', required: true },
              { label: 'First Guest Name *', name: 'first_guest_name', type: 'text', required: true },
              { label: 'Email *', name: 'email', type: 'email', required: true },
              { label: 'Check In Date *', name: 'check_in_date', type: 'date', required: true },
              { label: 'Check Out Date', name: 'check_out_date', type: 'date', required: false },
              { label: 'Mobile No *', name: 'mobile_no', type: 'number', required: true },
              { label: 'Total No. of Persons *', name: 'total_persons', type: 'text', required: true }
            ].map((input) => (
              <div key={input.name} className="flex flex-col gap-1">
                <label>{input.label}</label>
                <input
                  type={input.type}
                  name={input.name}
                  value={bookingData[input.name]}
                  onChange={handleBookingChange}
                  className="p-2 rounded text-black"
                  required={input.required}
                />
              </div>
            ))}

              {/* Night  */}
              <div className="flex flex-col gap-1">
                <label> No. of Night Stay *</label>
                 
                <select
                  name="no_of_night_stay"
                  onChange={handleBookingChange}
                  className="p-2 rounded text-black"
                  required
                >
                  <option value="">Select</option>
                  {[0, 1, 2, 3, 4, 5, 6].map((n) => (
                    <option key={n}>{n}</option>
                  ))}
                </select>
              </div>

              {/* Extra Bed */}
              <div className="flex flex-col gap-1">
                <label>Extra Bed</label>
                <select
                  name="extra_bed"
                  value={bookingData.extra_bed}
                  onChange={handleBookingChange}
                  className="p-2 rounded text-black"
                >
                  <option value="">Select</option>
                  {[0, 1, 2, 3, 4, 5, 6].map((n) => (
                    <option key={n}>{n}</option>
                  ))}
                </select>
              </div>

              {/* Child Without Bed */}
              <div className="flex flex-col gap-1">
                <label>Child without Bed</label>
                <select
                  name="child_without_bed"
                  value={bookingData.child_without_bed}
                  onChange={handleBookingChange}
                  className="p-2 rounded text-black"
                >
                  <option value="">Select</option>
                  {[0, 1, 2, 3, 4, 5, 6].map((n) => (
                    <option key={n}>{n}</option>
                  ))}
                </select>
              </div>

              {/* Meal Plan */}
              <div className="flex flex-col gap-1">
                <label>Meal Plan *</label>
                <select
                  name="meal_plan"
                  value={bookingData.meal_plan}
                  onChange={handleBookingChange}
                  className="p-2 rounded text-black"
                  required
                >
                  <option value="">Select</option>
                  <option>Room with no meal</option>
                  <option>Breakfast</option>
                  <option>Breakfast and Dinner</option>
                  <option>Breakfast with Lunch & Dinner</option>
                </select>
              </div>

              {/* Additional Info */}
              <div className="flex flex-col gap-1 col-span-1 md:col-span-2">
                <label>Additional Information</label>
                <textarea
                  name="additional_info"
                  value={bookingData.additional_info}
                  onChange={handleBookingChange}
                  className="p-2 rounded text-black"
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting}
                className="col-span-1 md:col-span-2 bg-[#246e73] py-2 rounded text-white font-bold hover:bg-white hover:text-[#246e73] transition"
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
            </form>

            {/* Response Message */}
            {responseMsg && (
              <p className="text-center mt-4 font-semibold text-yellow-300">
                {responseMsg}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default PackageDetails;
